import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

export interface Task {
  id: string;
  title: string;
  instructions: string;
  knowledge: unknown;
  status: 'todo' | 'in-progress' | 'paused' | 'in-review' | 'complete';
  activeInstanceId: string | null;
  assignedAgentId: string | null;
  projectId: string;
  createdAt: string;
  updatedAt: string;
}

export function useTasks(projectId: string | undefined) {
  return useQuery<Task[]>({
    queryKey: ['projects', projectId, 'tasks'],
    queryFn: async () => {
      const res = await api.get(`/api/projects/${projectId}/tasks`);
      return res.data;
    },
    enabled: !!projectId,
  });
}

export function useTask(id: string | undefined) {
  return useQuery<Task>({
    queryKey: ['tasks', id],
    queryFn: async () => {
      const res = await api.get(`/api/tasks/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreateTask(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { title: string; instructions?: string }) => {
      const res = await api.post(`/api/projects/${projectId}/tasks`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tasks'] });
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string; title?: string; instructions?: string; status?: Task['status']; assignedAgentId?: string | null }) => {
      const res = await api.put(`/api/tasks/${id}`, data);
      return res.data;
    },
    onSuccess: (data: Task) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', data.id] });
      queryClient.invalidateQueries({ queryKey: ['projects', data.projectId, 'tasks'] });
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/tasks/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
